import {
  Badge,
  Box,
  InputAdornment,
  List,
  ListItemButton,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { Virtuoso, VirtuosoHandle } from "react-virtuoso";
import { useTranslation } from "react-i18next";
import { messages } from "../../../../locales/messages";
import { ItemActivityGroup } from "./ChangesByItem/useReviewActivity";
import { ItemSummary } from "./ChangesByItem/useFlyerItemSummaries";
import { reviewTokens } from "../theme";
import { LazyThumb } from "./LazyThumb";

interface Props {
  groups: ItemActivityGroup[];
  summaries: Map<string, ItemSummary | "loading">;
  selectedFlyerItemId?: string;
  onSelect: (flyerItemId: string) => void;
}

interface NavRowProps {
  group: ItemActivityGroup;
  name: string;
  thumb?: string;
  selected: boolean;
  loading: boolean;
  fieldsHint: string;
  onSelect: (flyerItemId: string) => void;
}

const NavRow = memo(function NavRow({
  group,
  name,
  thumb,
  selected,
  loading,
  fieldsHint,
  onSelect,
}: NavRowProps) {
  const pending = group.counts.pending;
  const openComments = group.counts.openComments;
  const totalChanges = group.changeRequests.length;
  const totalComments = group.comments.length;

  const fallbackId = group.flyerItemId.slice(0, 8);
  const hasName = Boolean(name && name.trim().length > 0);

  return (
    <ListItemButton
      onClick={() => onSelect(group.flyerItemId)}
      selected={selected}
      sx={{
        position: "relative",
        alignItems: "flex-start",
        py: 1.25,
        pl: 2,
        pr: 1.5,
        borderBottom: 1,
        borderColor: "divider",
        gap: 1.25,
        "&::before": {
          content: '""',
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 3,
          bgcolor: selected ? "primary.main" : "transparent",
          transition: "background-color 0.15s ease",
        },
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          flexShrink: 0,
          borderRadius: `${reviewTokens.radiusSm}px`,
          border: `1px solid ${reviewTokens.border}`,
          overflow: "hidden",
          opacity: loading ? 0.6 : 1,
          transition: "opacity 0.2s ease",
        }}
      >
        <LazyThumb
          src={thumb}
          size={36}
          fit="cover"
          fallback={
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: reviewTokens.slate500,
                fontSize: 14,
              }}
            >
              {hasName ? name[0]?.toUpperCase() : "·"}
            </Box>
          }
        />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {hasName ? (
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              color: "text.primary",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={name}
          >
            {name}
          </Typography>
        ) : (
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <Typography
              variant="body2"
              sx={{
                fontFamily: reviewTokens.fontMono,
                fontWeight: 500,
                fontSize: 12,
                color: loading ? "text.secondary" : "text.primary",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={group.flyerItemId}
            >
              {fallbackId}…
            </Typography>
            {loading ? (
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  bgcolor: reviewTokens.slate300,
                  animation: "navrow-pulse 1.4s ease-in-out infinite",
                  "@keyframes navrow-pulse": {
                    "0%, 100%": { opacity: 0.35 },
                    "50%": { opacity: 1 },
                  },
                }}
              />
            ) : null}
          </Stack>
        )}

        {fieldsHint ? (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              color: "text.secondary",
              mt: 0.25,
              fontSize: 11,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={fieldsHint}
          >
            {fieldsHint}
          </Typography>
        ) : null}

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ mt: 0.5 }}
        >
          {totalChanges > 0 ? (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  bgcolor: pending > 0 ? "warning.main" : "success.main",
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: pending > 0 ? "warning.main" : "text.secondary",
                  fontWeight: 600,
                  fontSize: 11,
                }}
                title={
                  pending > 0
                    ? `${pending} pending of ${totalChanges} changes`
                    : `${totalChanges} changes`
                }
              >
                {pending > 0 ? `${pending}/${totalChanges}` : totalChanges}
              </Typography>
            </Stack>
          ) : null}
          {totalComments > 0 ? (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  bgcolor: reviewTokens.info,
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontWeight: 500,
                  fontSize: 11,
                }}
                title={
                  openComments > 0
                    ? `${openComments} open of ${totalComments} comments`
                    : `${totalComments} comments`
                }
              >
                {openComments > 0
                  ? `${openComments}/${totalComments}`
                  : totalComments}
              </Typography>
            </Stack>
          ) : null}
          {totalChanges === 0 && totalComments === 0 ? (
            <Typography variant="caption" sx={{ color: "text.disabled" }}>
              —
            </Typography>
          ) : null}
        </Stack>
      </Box>
    </ListItemButton>
  );
});

const buildFieldsHint = (group: ItemActivityGroup): string => {
  const seen = new Set<string>();
  const keys: string[] = [];
  for (const cr of group.changeRequests) {
    const fk = cr.fieldKey != null ? String(cr.fieldKey) : "";
    if (fk && !seen.has(fk)) {
      seen.add(fk);
      keys.push(fk);
      if (keys.length >= 3) break;
    }
  }
  const more = group.changeRequests.length - keys.length;
  if (keys.length === 0) return "";
  return more > 0 ? `${keys.join(" · ")} +${more}` : keys.join(" · ");
};

export function ItemNavList({
  groups,
  summaries,
  selectedFlyerItemId,
  onSelect,
}: Props) {
  const { t } = useTranslation();
  const theme = useTheme();
  const wide = useMediaQuery(theme.breakpoints.up("lg"));
  const [query, setQuery] = useState("");
  const virtuosoRef = useRef<VirtuosoHandle>(null);
  // Tracks the last id the user explicitly clicked, so the auto-select
  // scroll-into-view below never yanks the list after a manual selection.
  const lastClickedRef = useRef<string | undefined>(undefined);

  const handleSelect = useMemo(
    () => (flyerItemId: string) => {
      lastClickedRef.current = flyerItemId;
      onSelect(flyerItemId);
    },
    [onSelect],
  );

  const rows = useMemo(() => {
    return groups.map((g) => {
      const sum = summaries.get(g.flyerItemId);
      const loading = !sum || sum === "loading";
      const name =
        sum && sum !== "loading" && !sum.error ? sum.name || "" : "";
      const thumb =
        sum && sum !== "loading" && !sum.error ? sum.thumbnailUrl : undefined;
      const fieldsHint = buildFieldsHint(g);
      return { group: g, name, thumb, loading, fieldsHint };
    });
  }, [groups, summaries]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        r.fieldsHint.toLowerCase().includes(q) ||
        r.group.flyerItemId.toLowerCase().includes(q),
    );
  }, [rows, query]);

  // Bug fix: when an item is auto-selected (not user-clicked) it may be
  // outside the rendered window — bring it into view. A manual click is
  // already visible, so skip the scroll in that case.
  useEffect(() => {
    if (!selectedFlyerItemId) return;
    if (selectedFlyerItemId === lastClickedRef.current) return;
    const index = filtered.findIndex(
      (r) => r.group.flyerItemId === selectedFlyerItemId,
    );
    if (index >= 0) {
      virtuosoRef.current?.scrollToIndex({ index, align: "center" });
    }
  }, [selectedFlyerItemId, filtered]);

  return (
    <Box
      data-testid="item-nav"
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: `${reviewTokens.radiusLg}px`,
        bgcolor: "background.paper",
        display: "flex",
        flexDirection: "column",
        height: { lg: "calc(100vh - 240px)" },
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: "divider",
          position: "sticky",
          top: 0,
          bgcolor: "background.paper",
          zIndex: 1,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1 }}
        >
          <Typography variant="subtitle2">
            {t(messages.Review_Item_List())}
          </Typography>
          <Badge
            badgeContent={groups.length}
            color="default"
            sx={{
              "& .MuiBadge-badge": {
                position: "static",
                transform: "none",
                bgcolor: reviewTokens.slate100,
                color: "text.secondary",
                fontWeight: 600,
                fontSize: 11,
                height: 20,
                minWidth: 20,
                borderRadius: `${reviewTokens.radiusSm}px`,
                padding: "0 6px",
              },
            }}
          />
        </Stack>
        <TextField
          fullWidth
          size="small"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t(messages.Search())}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16, color: "text.secondary" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              fontSize: "0.8125rem",
            },
          }}
        />
      </Box>

      {filtered.length === 0 ? (
        <Box sx={{ overflow: "auto", flex: 1 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ p: 2, textAlign: "center" }}
          >
            {groups.length === 0
              ? t(messages.No_Items_With_Changes_Yet())
              : t(messages.No_Comments_Match_Search())}
          </Typography>
        </Box>
      ) : wide ? (
        <Virtuoso
          ref={virtuosoRef}
          data={filtered}
          computeItemKey={(_, r) => r.group.flyerItemId}
          overscan={400}
          style={{ flex: 1, minHeight: 0 }}
          itemContent={(_, { group, name, thumb, loading, fieldsHint }) => (
            <NavRow
              group={group}
              name={name}
              thumb={thumb}
              loading={loading}
              fieldsHint={fieldsHint}
              selected={group.flyerItemId === selectedFlyerItemId}
              onSelect={handleSelect}
            />
          )}
        />
      ) : (
        <Box sx={{ overflow: "auto", flex: 1 }}>
          <List dense disablePadding>
            {filtered.map(({ group, name, thumb, loading, fieldsHint }) => (
              <NavRow
                key={group.flyerItemId}
                group={group}
                name={name}
                thumb={thumb}
                loading={loading}
                fieldsHint={fieldsHint}
                selected={group.flyerItemId === selectedFlyerItemId}
                onSelect={handleSelect}
              />
            ))}
          </List>
        </Box>
      )}
    </Box>
  );
}
